import { Component, Inject, LOCALE_ID, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { run_bmi } from 'src/assets/dialogs/app.js';
import { Brand } from 'src/brands/brand';
import { ActiveUserService } from '../../services/activeuser.service';
import { Client, PutGamtestParams } from '../../generated-api-proxy';
import { EventPostParams } from '../../generated-api-proxy';
import { ParentMessengerService } from '../../services/parent-messenger.service';
import { CmsContentService } from '../../services/cms-content.service';

@Component({
  selector: 'app-dialogs',
  templateUrl: './dialogs.component.html',
  standalone: true,
  imports: [RouterLink],
  styleUrl: './dialogs.component.scss'
})
export class DialogsComponent implements OnDestroy {
  private bmi: string | null = null;
  heading = '';

  constructor(
    public route: ActivatedRoute,
    private router: Router,
    private client: Client,
    private activeUser: ActiveUserService,
    private parentMessengerService: ParentMessengerService,
    private cmsContentService: CmsContentService,
    private titleService: Title,
    @Inject(LOCALE_ID) private locale: string) {
    this.bmi = this.route.snapshot.paramMap.get('dialog');

    if (this.activeUser.LoggedIn()) {
      this.runDialog();
    } else {
      this.activeUser.loggedInChanged.subscribe(() => {
        this.runDialog();
      });
    }
  }

  runDialog() {
    this.client.results().subscribe({
      next: result => {
        let riskLevel = result?.total?.group ?? 1;

        this.client.interventions().subscribe({
          next: result => {
            let spendingInCent = result.wins - result.bets;

            let spending = spendingInCent / 100;

            this.load(riskLevel, spending);
          },

          error: _ => {
            let riskLevel = 1;

            let inputValue = 0;

            this.load(riskLevel, inputValue);
          }

        });
      },

      error: _ => {
        let riskLevel = 1;

        let inputValue = 0;

        this.load(riskLevel, inputValue);
      }
    });
  }

  load(riskLevel: number, inputValue: number) {
    const bmiKey = this.bmi ?? "missing-dialog-name";

    forkJoin({
      dialog: this.cmsContentService.getDialog(bmiKey),
      uiTexts: this.cmsContentService.getAll(this.locale, 'ChromeLabel')
    }).subscribe(({ dialog, uiTexts }) => {
      if (!dialog) {
        this.router.navigate(['/error']);
        return;
      }

      // Texts the dialog runtime reads via componentHelpers.uiText()
      const dialogTexts = Object.fromEntries(uiTexts
        .filter(t => t.key.startsWith('si_pts_dialog_'))
        .map(t => [t.key, t.text]));

      this.heading = dialogTexts[`si_pts_dialog_${bmiKey}_heading`] ?? '';
      if (this.heading)
        this.titleService.setTitle(`${this.heading} - ${Brand.brandTitle}`);

      let params = {
        bmiName: bmiKey,
        languageCultureName: this.locale,
        riskLevel: riskLevel,
        accountIndex: "0000",
        monthlySpending: 999,
        contentUrl: Brand.dialogUrl,
        inputValue: inputValue,
        uiTexts: dialogTexts
      };

      if (this.bmi == "gamtest")
        this.client.gamtests().subscribe(() => {
        });

      run_bmi(dialog, params);
    });
  }

  ngOnDestroy(): void {
    this.titleService.setTitle(Brand.brandTitle);
  }

  onDialogEvent(event: any): void {
    if (event.detail.key == "gamtest") {
      this.handleGamtestEvent(event);
    } else {
      this.handleBmiEvent(event);
    }
  }

  handleGamtestEvent(event: any): void {
    if (event?.detail?.payload?.url) {
      this.client.gamtestsFinish().subscribe(() => {
        this.router.navigate([event.detail.payload.url]);
      });
      return;
    } else {
      let question = event?.detail?.payload?.key ?? "unknown";
      let params = new PutGamtestParams();
      params.answer = event.detail.payload.value;

      this.client.gamtestsQuestions(question, params).subscribe({
        next: () => {
        },
        error: (err) => {
          console.error(`Error posting gamtest question: ${err}`);
          this.router.navigate(['/error']);
        }
      });
    }
  }

  handleBmiEvent(event: any): void {
    if (event?.detail?.payload?.url) {
      let eventPostParams = new EventPostParams();
      eventPostParams.source_id = event.detail.key;
      eventPostParams.type = "clicked_" + event.detail.payload.url;
      this.client.events(eventPostParams).subscribe(() => {
        if (event?.detail?.payload?.type == "parent") {
          this.parentMessengerService.navigate(event.detail.payload.url);
        } else if (event?.detail?.payload?.type == "external") {
          window.open(event.detail.payload.url, "_blank");
        } else if (event?.detail?.payload?.type == "internal") {
          this.router.navigate([event.detail.payload.url])
        }
      });
      return;
    } else if (event?.detail?.payload?.redirect) {
      let target = event.detail.payload.redirect;
      this.router.navigate([target])
      return;
    }
    else {
      let eventPostParams = new EventPostParams();
      eventPostParams.source_id = event.detail.key;
      eventPostParams.type = "shown_" + event.detail.payload.shown;
      this.client.events(eventPostParams).subscribe({
        next: () => {
        },
        error: (err) => {
          console.error(`Error posting bmi event: ${err}`);
          this.router.navigate(['/error']);
        }
      });
    }
  }
}
